import { useMemo } from 'react';
import { Badge, BadgeType } from '@amzn/stencil-react-components/badge';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import {
  MessageBanner,
  MessageBannerType,
} from '@amzn/stencil-react-components/message-banner';
import { H1, Text } from '@amzn/stencil-react-components/text';
import { IconDownloadSmall } from '@amzn/stencil-react-icons';
import type {
  GetRiskScoreFilterOptionsCommandOutput,
  ListRiskScoresCommandOutput,
  RiskScoreFacetValue,
} from '@amzn/gbsa-helios-typescript-client';

import { useCurrentUser } from 'src/v2/shared/auth';
import { useMessages } from 'src/v2/shared/i18n/useMessages';
import { useIsMobile } from 'src/v2/shared/ui/useIsMobile';
import { useGetRiskScoreFilterOptionsQuery, useListRiskScoresQuery } from '../api';
import { useRiskEngineListFilters, useRiskScoreExport } from '../hooks';
import {
  toCategoryFilterOptions,
  toMetricFilterOptions,
  toStatusFilterOptions,
  type RiskFilterOption,
} from '../logic/riskFilters';
import { RISK_ENGINE_BUNDLE, type RiskEngineMessageKey } from '../translations';
import { RISK_ENGINE_TEST_IDS } from '../testIds';
import { RiskEngineFilterBar } from './RiskEngineFilterBar';
import { RiskEnginePageSkeleton } from './RiskEnginePageSkeleton';
import { RISK_TABLE_COLUMN_COUNT, RiskMetricsTable } from './RiskMetricsTable';

/** Stable empty references so a load-pending filter bar doesn't churn child props. */
const EMPTY_OPTIONS: readonly RiskFilterOption[] = [];
const EMPTY_FACETS: readonly RiskScoreFacetValue[] = [];

interface RiskFilterBarOptions {
  readonly categoryOptions: readonly RiskFilterOption[];
  readonly metricOptions: readonly RiskFilterOption[];
  readonly statusOptions: readonly RiskFilterOption[];
}

/** Maps the faceted options response to the filter bar's option lists, each counted. */
function buildFilterBarOptions(
  optionsData: GetRiskScoreFilterOptionsCommandOutput | undefined,
  t: (key: RiskEngineMessageKey) => string,
  f: (key: RiskEngineMessageKey, args: Record<string, unknown>) => string,
): RiskFilterBarOptions {
  const label = (name: string, count: number): string =>
    f('filterOptionWithCount', { label: name, count });
  return {
    categoryOptions: optionsData
      ? toCategoryFilterOptions(optionsData.categories, label)
      : EMPTY_OPTIONS,
    metricOptions: optionsData
      ? toMetricFilterOptions(optionsData.metrics, label)
      : EMPTY_OPTIONS,
    statusOptions: toStatusFilterOptions(optionsData?.statuses ?? EMPTY_FACETS, t, label),
  };
}

interface RiskEmptyState {
  readonly showNoBusinessUnitData: boolean;
  readonly showNoLocationData: boolean;
  readonly showNoFilterResults: boolean;
  readonly showTable: boolean;
}

/**
 * Resolves which (if any) business-level empty state to show. An empty live day with active facet
 * filters reads as "filters matched nothing"; without them it keeps the saved-locations copy. The
 * table renders in every other state (it owns its own loading/error/empty rendering).
 */
function resolveRiskEmptyState(
  settled: boolean,
  data: ListRiskScoresCommandOutput | undefined,
  hasActiveFacetFilters: boolean,
): RiskEmptyState {
  const showNoBusinessUnitData = settled && data?.hasData === false;
  const noRowsOnLiveDay =
    settled && data?.hasData === true && (data?.totalCount ?? 0) === 0;
  const showNoFilterResults = noRowsOnLiveDay && hasActiveFacetFilters;
  const showNoLocationData = noRowsOnLiveDay && !hasActiveFacetFilters;
  return {
    showNoBusinessUnitData,
    showNoLocationData,
    showNoFilterResults,
    showTable: !showNoBusinessUnitData && !showNoLocationData && !showNoFilterResults,
  };
}

interface RiskEngineEmptyStateProps {
  readonly showNoBusinessUnitData: boolean;
  readonly showNoLocationData: boolean;
  readonly showNoFilterResults: boolean;
  readonly t: (key: RiskEngineMessageKey) => string;
}

function RiskEngineEmptyState({
  showNoBusinessUnitData,
  showNoLocationData,
  showNoFilterResults,
  t,
}: RiskEngineEmptyStateProps): JSX.Element | null {
  if (showNoBusinessUnitData) {
    return (
      <Col
        alignItems="center"
        dataTestId={RISK_ENGINE_TEST_IDS.emptyState}
        padding="dimensions.spacing.500"
      >
        <Text color="color.text.secondary">{t('noBusinessUnitData')}</Text>
      </Col>
    );
  }
  if (showNoFilterResults) {
    return (
      <Col
        alignItems="center"
        dataTestId={RISK_ENGINE_TEST_IDS.noResults}
        padding="dimensions.spacing.500"
      >
        <Text color="color.text.secondary">{t('noFilterResults')}</Text>
      </Col>
    );
  }
  if (showNoLocationData) {
    return (
      <Col
        alignItems="center"
        dataTestId={RISK_ENGINE_TEST_IDS.emptyState}
        padding="dimensions.spacing.500"
      >
        <Text color="color.text.secondary">{t('noLocationData')}</Text>
      </Col>
    );
  }
  return null;
}

/** Feature-gated Risk Engine list shared by the mobile and desktop shells. */
export function RiskEnginePage(): JSX.Element {
  const { businessUnit, savedLocations } = useCurrentUser();
  const {
    t,
    f,
    isLoading: isBundleLoading,
  } = useMessages<RiskEngineMessageKey>(RISK_ENGINE_BUNDLE);
  const isMobile = useIsMobile();

  const {
    activeFilters,
    requestLocations,
    sort,
    currentPage,
    pageSize,
    listRequest,
    filterOptionsRequest,
    onApplyFilters,
    onSort,
    onPageChange,
  } = useRiskEngineListFilters({ businessUnit, savedLocations });

  const query = useListRiskScoresQuery(listRequest, { enabled: !!businessUnit });
  const { data, isLoading, isPending, isError } = query;

  // Faceted filter options — refetches (debounced) as the selection narrows.
  const filterOptionsQuery = useGetRiskScoreFilterOptionsQuery(filterOptionsRequest, {
    enabled: !!businessUnit,
  });
  const optionsData = filterOptionsQuery.data;

  const { categoryOptions, metricOptions, statusOptions } = useMemo(
    () => buildFilterBarOptions(optionsData, t, f),
    [optionsData, t, f],
  );

  // Desktop-only CSV export. Scoped to the business unit, window, and locations only — it exports
  // every metric/category/status in that scope and deliberately ignores the table's facet filters.
  const {
    triggerExport,
    isExporting,
    showError: showExportError,
    showEmpty: showExportEmpty,
  } = useRiskScoreExport({
    businessUnitId: businessUnit,
    timeWindow: activeFilters.timeWindow,
    locations: requestLocations,
  });

  const settled = !isLoading && !isPending && !isError;
  const hasActiveFacetFilters =
    activeFilters.categories.length > 0 ||
    activeFilters.metrics.length > 0 ||
    activeFilters.statuses.length > 0;
  const { showNoBusinessUnitData, showNoLocationData, showNoFilterResults, showTable } =
    resolveRiskEmptyState(settled, data, hasActiveFacetFilters);

  // Until the translations load, every label would be a raw message key — show the page skeleton
  // instead. The list and filter-option requests above are already in flight.
  if (isBundleLoading) {
    return (
      <RiskEnginePageSkeleton
        columnCount={RISK_TABLE_COLUMN_COUNT}
        dataTestId={RISK_ENGINE_TEST_IDS.root}
      />
    );
  }

  return (
    <Col
      dataTestId={RISK_ENGINE_TEST_IDS.root}
      gridGap="dimensions.spacing.300"
      minWidth={0}
      padding={{
        top: 'dimensions.spacing.300',
        bottom: 'dimensions.spacing.600',
        left: 'dimensions.spacing.200',
        right: 'dimensions.spacing.200',
      }}
      width="100%"
    >
      <Col gridGap="dimensions.spacing.50" minWidth={0}>
        <Row
          justifyContent="space-between"
          alignItems="center"
          gridGap="dimensions.spacing.300"
        >
          <H1>{t('pageTitle')}</H1>
          {!isMobile ? (
            <Button
              variant={ButtonVariant.Secondary}
              icon={<IconDownloadSmall aria-hidden />}
              disabled={!businessUnit || isExporting}
              onClick={triggerExport}
              dataTestId={RISK_ENGINE_TEST_IDS.exportButton}
            >
              {isExporting ? t('exportInProgress') : t('exportButton')}
            </Button>
          ) : null}
        </Row>
        <Text color="color.text.secondary">{t('pageDescription')}</Text>
        {data?.scoringDate ? (
          <Row>
            <Badge
              type={BadgeType.Primary}
              value={f('scoredDate', { date: data.scoringDate })}
            />
          </Row>
        ) : null}
      </Col>

      {!isMobile && showExportError ? (
        <MessageBanner
          type={MessageBannerType.Error}
          isDismissible={false}
          dataTestId={RISK_ENGINE_TEST_IDS.exportError}
        >
          {t('exportError')}
        </MessageBanner>
      ) : null}

      {!isMobile && showExportEmpty ? (
        <MessageBanner
          type={MessageBannerType.Warning}
          isDismissible={false}
          dataTestId={RISK_ENGINE_TEST_IDS.exportEmpty}
        >
          {t('exportEmpty')}
        </MessageBanner>
      ) : null}

      <RiskEngineFilterBar
        filters={activeFilters}
        locationOptions={savedLocations}
        categoryOptions={categoryOptions}
        metricOptions={metricOptions}
        statusOptions={statusOptions}
        onApply={onApplyFilters}
      />

      {showTable ? (
        <Col width="100%">
          <RiskMetricsTable
            query={query}
            ariaLabel={t('tableLabel')}
            t={t}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={onPageChange}
            activeSortId={sort.activeSortId}
            sortDirection={sort.sortDirection}
            onSort={onSort}
            activeTimeWindow={activeFilters.timeWindow}
          />
        </Col>
      ) : null}

      <RiskEngineEmptyState
        showNoBusinessUnitData={showNoBusinessUnitData}
        showNoLocationData={showNoLocationData}
        showNoFilterResults={showNoFilterResults}
        t={t}
      />
    </Col>
  );
}
